import { useCallback, useEffect, useState, useMemo } from 'react';

import { Row, Col, Flex, Card, Typography, Skeleton } from 'antd';

import { AnalyticsCard } from '../campaign-reports/AnalyticsCard';
import { AnalyticsCardDateFilter } from '../campaign-reports/AnalyticsCardDateFilter';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isBetween);
dayjs.extend(utc);
import { AnalyticsInfoCard } from '../campaign-reports/AnalyticsInfoCard';

import { useConsumerRegistrationDeletion } from '../../hooks/useConsumerRegistrationDeletion';
import { useReports } from '../../hooks/useReports';
import type { ConsumerRegistrationDeletionResponse } from '../../schemas/auth';
import type { ParsedChartItem } from '../../interface/campaign-analytics';
import PageWrapper from '../../components/PageWrapper';

interface GroupedData {
  date: string;
  registerCount: number;
  deleteAccountCount: number;
  diff: number;
}

export default function UserReports() {
  const outputFormat = 'YYYY-MM-DD';

  // Interval gruplama fonksiyonları
  // Tüm günleri 0 değerleriyle dolduran yardımcı fonksiyon
  const generateEmptyDatesForRange = useCallback((dateRange: [Dayjs | null, Dayjs | null], interval: string): GroupedData[] => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return [];

    const result: GroupedData[] = [];

    switch (interval) {
      case 'Weekly': {
        let currentWeekStart = startDate.startOf('week');
        while (currentWeekStart.isBefore(endDate) || currentWeekStart.isSame(endDate, 'week')) {
          result.push({
            date: currentWeekStart.format('YYYY-MM-DD'),
            registerCount: 0,
            deleteAccountCount: 0,
            diff: 0,
          });
          currentWeekStart = currentWeekStart.add(1, 'week');
        }
        break;
      }
      case 'Monthly': {
        let currentMonthStart = startDate.startOf('month');
        while (currentMonthStart.isBefore(endDate) || currentMonthStart.isSame(endDate, 'month')) {
          result.push({
            date: currentMonthStart.format('YYYY-MM-DD'),
            registerCount: 0,
            deleteAccountCount: 0,
            diff: 0,
          });
          currentMonthStart = currentMonthStart.add(1, 'month');
        }
        break;
      }
      case 'Daily':
      default: {
        let currentDate = startDate;
        while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
          result.push({
            date: currentDate.format('YYYY-MM-DD'),
            registerCount: 0,
            deleteAccountCount: 0,
            diff: 0,
          });
          currentDate = currentDate.add(1, 'day');
        }
        break;
      }
    }

    return result;
  }, []);

  const groupDataByInterval = useCallback(
    (data: ConsumerRegistrationDeletionResponse, interval: string, dateRange: [Dayjs | null, Dayjs | null]): GroupedData[] => {
      if (!data || data.length === 0) {
        // Veri yoksa bile tüm günleri 0 değerleriyle doldur
        return generateEmptyDatesForRange(dateRange, interval);
      }

      const [startDate, endDate] = dateRange;
      if (!startDate || !endDate) return [];

      switch (interval) {
        case 'Weekly':
          return groupByWeekly(data, startDate, endDate);
        case 'Monthly':
          return groupByMonthly(data, startDate, endDate);
        case 'Daily':
        default:
          return groupByDaily(data, startDate, endDate);
      }
    },
    [generateEmptyDatesForRange]
  );

  const groupByDaily = (data: ConsumerRegistrationDeletionResponse, startDate: Dayjs, endDate: Dayjs): GroupedData[] => {
    const result: GroupedData[] = [];
    let currentDate = startDate;

    // Tüm günleri dolaş ve her gün için veri oluştur
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const currentDateStr = currentDate.format('YYYY-MM-DD');

      // Bu tarihe ait veriyi bul (timezone'dan bağımsız karşılaştırma)
      const existingData = data.find((item) => {
        // T00:00:00 kısmını çıkararak sadece tarih kısmını al
        const itemDate = item.date.split('T')[0];
        return itemDate === currentDateStr;
      });

      // Var olan veriyi kullan, yoksa 0 değerleriyle oluştur
      result.push({
        date: currentDateStr,
        registerCount: existingData?.registerCount || 0,
        deleteAccountCount: existingData?.deleteAccountCount || 0,
        diff: existingData?.diff || 0,
      });

      currentDate = currentDate.add(1, 'day');
    }

    return result;
  };

  const groupByWeekly = (data: ConsumerRegistrationDeletionResponse, startDate: Dayjs, endDate: Dayjs): GroupedData[] => {
    const weeks: GroupedData[] = [];
    let currentWeekStart = startDate.startOf('week');

    while (currentWeekStart.isBefore(endDate) || currentWeekStart.isSame(endDate, 'week')) {
      const currentWeekEnd = currentWeekStart.endOf('week');

      // Bu haftadaki verileri topla
      const weekData = data.filter((item) => {
        // T00:00:00 kısmını çıkararak sadece tarih kısmını al
        const itemDate = dayjs(item.date.split('T')[0]);
        return itemDate.isBetween(currentWeekStart, currentWeekEnd, 'day', '[]');
      });

      const weeklyRegisterCount = weekData.reduce((sum, item) => sum + item.registerCount, 0);
      const weeklyDeleteAccountCount = weekData.reduce((sum, item) => sum + item.deleteAccountCount, 0);

      weeks.push({
        date: currentWeekStart.format('YYYY-MM-DD'),
        registerCount: weeklyRegisterCount,
        deleteAccountCount: weeklyDeleteAccountCount,
        diff: weeklyRegisterCount - weeklyDeleteAccountCount,
      });

      currentWeekStart = currentWeekStart.add(1, 'week');
    }

    return weeks;
  };

  const groupByMonthly = (data: ConsumerRegistrationDeletionResponse, startDate: Dayjs, endDate: Dayjs): GroupedData[] => {
    const months: GroupedData[] = [];
    let currentMonthStart = startDate.startOf('month');

    while (currentMonthStart.isBefore(endDate) || currentMonthStart.isSame(endDate, 'month')) {
      const currentMonthEnd = currentMonthStart.endOf('month');

      // Bu aydaki verileri topla
      const monthData = data.filter((item) => {
        // T00:00:00 kısmını çıkararak sadece tarih kısmını al
        const itemDate = dayjs(item.date.split('T')[0]);
        return itemDate.isBetween(currentMonthStart, currentMonthEnd, 'day', '[]');
      });

      const monthlyRegisterCount = monthData.reduce((sum, item) => sum + item.registerCount, 0);
      const monthlyDeleteAccountCount = monthData.reduce((sum, item) => sum + item.deleteAccountCount, 0);

      months.push({
        date: currentMonthStart.format('YYYY-MM-DD'),
        registerCount: monthlyRegisterCount,
        deleteAccountCount: monthlyDeleteAccountCount,
        diff: monthlyRegisterCount - monthlyDeleteAccountCount,
      });

      currentMonthStart = currentMonthStart.add(1, 'month');
    }

    return months;
  };

  const userRegistrationChartOptions = useMemo(
    () => ({
      registerCountId: 'Number of new member accounts',
      registerCountColor: '#00ab81', // Primary renk
      deleteAccountCountId: 'Number of deleted accounts',
      deleteAccountCountColor: '#dc3545', // Kırmızı renk
    }),
    []
  );

  const last7Days = dayjs().subtract(6, 'day');

  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);

  const [userReportsLoading, setUserReportsLoading] = useState<boolean>(true);
  const [userReportsInterval, setUserReportsInterval] = useState<string>('Daily');
  const [userReportsFilteredDates, setUserReportsFilteredDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [userReportsDates, setUserReportsDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [userReportsData, setUserReportsData] = useState<{
    items: ParsedChartItem[];
    totalRegisterCount: number;
    totalDeleteAccountCount: number;
    rawData: ConsumerRegistrationDeletionResponse;
  }>();

  const { data: consumerData, isLoading: consumerLoading } = useConsumerRegistrationDeletion({
    minDate: userReportsFilteredDates[0]?.format(outputFormat),
    maxDate: userReportsFilteredDates[1]?.format(outputFormat),
  });

  const { data: activeUsersCount, isLoading: activeUsersLoading } = useReports({ status: 'Active' });
  const { data: passiveUsersCount, isLoading: passiveUsersLoading } = useReports({ status: 'Passive' });
  const { data: deletedUsersCount, isLoading: deletedUsersLoading } = useReports({ status: 'Deleted' });

  useEffect(() => {
    setLoadingStatus(true);

    setLoadingStatus(false);
  }, []);

  const parsedUserRegistrationChartData = useCallback(
    (items: GroupedData[]): ParsedChartItem[] => {
      return [
        {
          id: userRegistrationChartOptions.registerCountId,
          color: userRegistrationChartOptions.registerCountColor,
          data: items.map((item) => ({
            // Timezone sorununu önlemek için tarihi UTC olarak parse et
            x: new Date(item.date + 'T00:00:00Z'),
            y: item.registerCount,
          })),
        },
        {
          id: userRegistrationChartOptions.deleteAccountCountId,
          color: userRegistrationChartOptions.deleteAccountCountColor,
          data: items.map((item) => ({
            // Timezone sorununu önlemek için tarihi UTC olarak parse et
            x: new Date(item.date + 'T00:00:00Z'),
            y: item.deleteAccountCount,
          })),
        },
      ];
    },
    [userRegistrationChartOptions]
  );

  // consumerData değiştiğinde chart'ı güncelle
  useEffect(() => {
    if (consumerData && Array.isArray(consumerData) && consumerData.length > 0) {
      // Interval'a göre veriyi grupla
      const groupedData = groupDataByInterval(consumerData, userReportsInterval, userReportsFilteredDates);

      const totalRegisterCount = consumerData.reduce((sum, item) => sum + item.registerCount, 0);
      const totalDeleteAccountCount = consumerData.reduce((sum, item) => sum + item.deleteAccountCount, 0);

      const parsedUserReportsResponse = {
        items: parsedUserRegistrationChartData(groupedData),
        totalRegisterCount,
        totalDeleteAccountCount,
        rawData: consumerData,
      };

      setUserReportsData(parsedUserReportsResponse);
    } else if (consumerData && Array.isArray(consumerData) && consumerData.length === 0) {
      // Boş array geldiğinde userReportsData'yı boş items ile güncelle
      setUserReportsData({
        items: [],
        totalRegisterCount: 0,
        totalDeleteAccountCount: 0,
        rawData: [],
      });
    }
    setUserReportsLoading(consumerLoading);
  }, [consumerData, consumerLoading, parsedUserRegistrationChartData, groupDataByInterval, userReportsInterval, userReportsFilteredDates]);

  const LoadingSkeleton = () => {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Row>
            <Col span={24}>
              <Flex
                gap={16}
                vertical
              >
                <Card className="custom-card child-card">
                  <Skeleton active />
                </Card>
              </Flex>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  if (loadingStatus) {
    return <LoadingSkeleton />;
  }

  return (
    <PageWrapper title="User Reports">
      <Row>
        <Col span={24}>
          <Flex
            vertical
            gap={24}
          >
            {/* Üst taraftaki Info Kartları */}
            <Flex
              gap={16}
              wrap
            >
              <AnalyticsInfoCard
                title="Total Registered User"
                isLegend={false}
                value={activeUsersCount !== undefined && passiveUsersCount !== undefined ? (activeUsersCount + passiveUsersCount).toString() : '-'}
                loading={activeUsersLoading || passiveUsersLoading}
                tooltipDescription="Total number of registered users"
                notTransaction={false}
              />
              <AnalyticsInfoCard
                title="Number of Deleted Users"
                isLegend={false}
                value={deletedUsersCount?.toString() || '-'}
                loading={deletedUsersLoading}
                tooltipDescription="Total number of deleted users"
                notTransaction={false}
              />
            </Flex>

            {/* Alt taraftaki Chart */}
            <AnalyticsCard
              chartData={userReportsData && userReportsData?.items.length > 0 ? userReportsData?.items : []}
              dates={userReportsDates}
              interval={userReportsInterval}
              timezone="UTC"
              title="User Metrics"
              extra={
                <AnalyticsCardDateFilter
                  pickerStartInputId="user-reports-start"
                  pickerEndInputId="user-reports-end"
                  filteredDates={userReportsFilteredDates}
                  setFilteredDates={setUserReportsFilteredDates}
                  dates={userReportsDates}
                  setDates={setUserReportsDates}
                  interval={userReportsInterval}
                  setInterval={setUserReportsInterval}
                  notTransaction={false}
                />
              }
            ></AnalyticsCard>
          </Flex>
        </Col>
      </Row>
    </PageWrapper>
  );
}
